import type { IFilesystem } from '@livx.cc/wcli/core';
import type { Hooks, ToolUse } from './hooks';
import { writeFact } from './memory';
import { forComponent } from './logging';

const log = forComponent('Lessons');

/**
 * Automatic mistake → lesson → steer loop. Closes the within-run learning gap: the agent
 * reacting to an error string is transient, but a RECURRING failure is a signal worth keeping.
 * This hook watches tool results for known failure patterns; once the SAME pattern recurs
 * `minRepeats` times in a run, it persists a corrective lesson to memory (deduped) — so the
 * NEXT session's `loadMemory` injects it as steering. Deterministic, edge-safe, no LLM call.
 *
 * Writes immediately on crossing the threshold (not on a clean stop), so a loop/budget kill —
 * exactly the runs that wasted effort — still leaves a lesson behind.
 */
export interface LessonOptions {
  fs: IFilesystem;
  /** Memory dir to persist lessons into (same dir loadMemory reads). */
  dir: string;
  /** How many times a pattern must recur before it's worth remembering. */
  minRepeats: number;
}

export class LessonOptionsDefaults implements Partial<LessonOptions> {
  minRepeats = 2;
}

/** Known failure substring → the corrective lesson to persist. Maps OUR own tool errors to fixes. */
const LESSONS: { match: RegExp; slug: string; body: string }[] = [
  { match: /changed since it was read|stale/i, slug: 'lesson-stale-edit', body: 'Edit kept failing because the file changed since it was read (stale). Re-Read a file immediately before Editing it — do not Read far ahead of the Edit.' },
  { match: /is not unique|\(\d+ matches\)/i, slug: 'lesson-edit-ambiguous', body: 'Edit kept failing because old_string was not unique. Include more surrounding lines to disambiguate, or batch with MultiEdit.' },
  { match: /has not been read yet/i, slug: 'lesson-read-before-edit', body: 'Edit was attempted before Reading the file. Always Read a file before Editing it.' },
  { match: /not found in/i, slug: 'lesson-edit-not-found', body: 'Edit old_string was not found. Re-Read the current file and copy the exact text (whitespace included) before Editing.' },
  { match: /unknown tool/i, slug: 'lesson-unknown-tool', body: 'A non-existent tool was called. Only call tools that are advertised in the current tool set.' },
];

const isFailure = (result: string): boolean => /^Error:|^Blocked by hook:|^\[exit [1-9]/.test(result);

/**
 * Build a Hooks object that auto-captures recurring failures as memory lessons.
 * Compose it with the host's own hooks via `composeHooks`.
 */
export function lessonCapture(options: LessonOptions): Hooks {
  const o = { ...new LessonOptionsDefaults(), ...options } as LessonOptions;
  const counts = new Map<string, number>();
  const written = new Set<string>();
  return {
    async postToolUse(_call: ToolUse, result: string) {
      if (!isFailure(result)) return;
      const lesson = LESSONS.find((l) => l.match.test(result));
      if (!lesson || written.has(lesson.slug)) return;
      const n = (counts.get(lesson.slug) ?? 0) + 1;
      counts.set(lesson.slug, n);
      if (n < o.minRepeats) return;
      written.add(lesson.slug); // write-once per run, on crossing the threshold
      await writeFact(o.fs, o.dir, lesson.slug, lesson.body).catch((e) => log.warn(`could not persist ${lesson.slug}: ${e?.message ?? e}`));
      log.debug(`captured lesson ${lesson.slug} (recurred ${n}×)`);
    },
  };
}
